import {BulbFilled, BulbOutlined, CloseOutlined, CodeOutlined, DeleteOutlined, FileTextOutlined, MessageFilled, MessageOutlined, RedoOutlined, SaveOutlined} from "@ant-design/icons";
import {Button, Card, Tag, Timeline, Tooltip} from "antd";
import React from "react";
import {Link} from "react-router-dom";
import {AiExtractionResult, ReasoningSummaryNode, ReasoningSummaryWebSearchOpenPageNode, VulSourceItemAiExtractorConfig} from "../../domain/VulSourceItem";
import {formatBytesBinary, syntaxHighlight, utf8ByteLength} from "../../utils/FormatUtils";
import {normalizeNonEmptyString} from "../../utils/StringUtils";
import * as globalStyles from "../App.module.css";
import {AiExtractorType, formatAiExtractorType, formatAiExtractorWebSearchConfig} from "../../domain/AiExtractor";
import {AiExtractorSource, formatAiExtractorSource} from "../../domain/AiExtractorSource";
import {AiAttributeRatingChip, AiAttributeRatingChipList} from "../common/AiAttributeRating";
import * as styles from "./AiExtractionResultsSection.module.css";
import AiRunMetrics, {AiRunMetric} from "../common/AiRunMetrics";

type Props = {
    aiExtractionResults?: AiExtractionResult[];
    actionMode?: "none" | "delete" | "preview";
    onDeleteAiExtractionResult?: (aiExtractionResultId: string) => void;
    onRunAgain?: (aiExtractionResult: AiExtractionResult) => void;
    canRunAgain?: (aiExtractionResult: AiExtractionResult) => boolean;
    onPreviewRunAgain?: (aiExtractionResult: AiExtractionResult) => void;
    onPreviewCancel?: (aiExtractionResult: AiExtractionResult) => void;
    onPreviewSave?: (aiExtractionResult: AiExtractionResult) => void;
    onShowAiProviderCommunicationLog?: (runId: string) => void;
    highlightAiExtractionResultId?: string;
    onHighlightedAiExtractionResultRendered?: (element: HTMLDivElement | null) => void;
    globalMessagesVisible?: boolean;
};

type AiExtractionResultAiAttributeRow = {
    name: string;
    kind: "rating" | "text" | "texts";
    percent?: number;
    valueText?: string;
    valueTexts?: string[];
    message?: string;
};

type AiExtractorConfigRow = {
    label: string;
    value?: string;
    standalone?: boolean;
};

type StatisticRow = {
    label: string;
    value: string;
};

const AI_EXTRACTOR_TYPES = Object.values(AiExtractorType) as string[];
const AI_EXTRACTOR_SOURCES = Object.values(AiExtractorSource) as string[];
const TOKEN_COUNT_FORMATTER = new Intl.NumberFormat();

function toPercent(value: number): number {
    const percent = value <= 1 ? value * 100 : value;
    return Math.max(0, Math.min(100, percent));
}

function toNumber(rawValue: unknown): number | undefined {
    if (typeof rawValue === "number") {
        return Number.isFinite(rawValue) ? rawValue : undefined;
    }

    if (typeof rawValue === "string") {
        const normalized = rawValue.trim().replace(",", ".");

        if (!normalized) {
            return undefined;
        }

        const numericValue = Number(normalized);

        return Number.isNaN(numericValue) ? undefined : numericValue;
    }

    if (rawValue && typeof rawValue === "object") {
        const obj = rawValue as Record<string, unknown>;
        const fields = ["score", "value", "valueRating", "confidence", "probability", "percent"];

        for (const field of fields) {
            const numericValue = toNumber(obj[field]);

            if (numericValue !== undefined) {
                return numericValue;
            }
        }
    }

    return undefined;
}

function toAiAttributeRows(aiAttributes?: unknown): AiExtractionResultAiAttributeRow[] {
    if (!aiAttributes) {
        return [];
    }

    if (typeof aiAttributes === "string") {
        try {
            return toAiAttributeRows(JSON.parse(aiAttributes));
        } catch {
            return [];
        }
    }

    const rows: AiExtractionResultAiAttributeRow[] = [];

    if (Array.isArray(aiAttributes)) {
        aiAttributes.forEach(item => {
            if (!item || typeof item !== "object") {
                return;
            }

            const obj = item as Record<string, unknown>;
            const rawName = obj.name ?? obj.className ?? obj.class ?? obj.label ?? obj.id;
            const name = typeof rawName === "string" ? rawName.trim() : "";

            if (!name) {
                return;
            }

            pushAiAttributeRow(rows, name, obj);
        });
    } else if (typeof aiAttributes === "object") {
        Object.entries(aiAttributes as Record<string, unknown>).forEach(([rawName, rawValue]) => {
            const name = rawName.trim();

            if (!name) {
                return;
            }

            pushAiAttributeRow(rows, name, rawValue);
        });
    }

    return rows.sort((a, b) => {
        if (a.kind === "rating" && b.kind !== "rating") {
            return -1;
        }

        if (a.kind !== "rating" && b.kind === "rating") {
            return 1;
        }

        return a.name.localeCompare(b.name);
    });
}

function pushAiAttributeRow(rows: AiExtractionResultAiAttributeRow[], name: string, rawValue: unknown) {
    const numericValue = toNumber(rawValue);
    const valueText = toAiAttributeValueText(rawValue);
    const valueTexts = toAiAttributeValueTexts(rawValue);

    if (numericValue !== undefined) {
        rows.push({
            name,
            kind: "rating",
            percent: toPercent(numericValue),
            message: toAiAttributeMessage(rawValue)
        });
        return;
    }

    if (valueText !== undefined) {
        rows.push({
            name,
            kind: "text",
            valueText,
            message: toAiAttributeMessage(rawValue)
        });
        return;
    }

    if (valueTexts !== undefined) {
        rows.push({
            name,
            kind: "texts",
            valueTexts,
            message: toAiAttributeMessage(rawValue)
        });
    }
}

function toAiAttributeMessage(rawValue: unknown): string | undefined {
    if (!rawValue || typeof rawValue !== "object") {
        return undefined;
    }

    const obj = rawValue as Record<string, unknown>;

    return normalizeNonEmptyString(obj.message)
        ?? normalizeNonEmptyString(obj.explanation)
        ?? normalizeNonEmptyString(obj.reason);
}

function toAiAttributeValueText(rawValue: unknown): string | undefined {
    if (!rawValue || typeof rawValue !== "object") {
        return undefined;
    }

    const obj = rawValue as Record<string, unknown>;

    return normalizeNonEmptyString(obj.valueSummary)
        ?? normalizeNonEmptyString(obj.valueText)
        ?? normalizeNonEmptyString(obj.text);
}

function toAiAttributeValueTexts(rawValue: unknown): string[] | undefined {
    if (!rawValue || typeof rawValue !== "object") {
        return undefined;
    }

    const obj = rawValue as Record<string, unknown>;
    const rawValues = Array.isArray(obj.valueTexts) ? obj.valueTexts : (Array.isArray(obj.texts) ? obj.texts : undefined);

    if (!rawValues) {
        return undefined;
    }

    return rawValues
        .map(value => normalizeNonEmptyString(value))
        .filter((value): value is string => value !== undefined);
}

function toDisplayValue(value: string): string {
    if (/^[A-Z0-9_]+$/.test(value)) {
        return value.toLowerCase().replace(/_/g, " ");
    }

    return value;
}

function buildReasoningSummary(summary?: ReasoningSummaryNode[]): ReasoningSummaryNode[] {
    if (!Array.isArray(summary)) {
        return [];
    }

    const entries: ReasoningSummaryNode[] = [];

    summary.forEach(item => {
        if (!item || typeof item !== "object") {
            return;
        }

        if (item.type === "reasoning") {
            const text = normalizeNonEmptyString(item.text);

            if (!text) {
                return;
            }

            entries.push({
                type: "reasoning",
                text
            });
            return;
        }

        if (item.type !== "websearch") {
            if (item.type === "unknown") {
                entries.push({
                    type: "unknown",
                    provider: normalizeNonEmptyString(item.provider),
                    kind: normalizeNonEmptyString(item.kind),
                    raw: item.raw
                });
            }

            return;
        }

        const status = normalizeNonEmptyString(item.status);

        if (item.action === "search") {
            const query = normalizeNonEmptyString(item.query);

            if (!query) {
                return;
            }

            const sources = Array.isArray(item.sources)
                ? item.sources
                    .map(source => normalizeNonEmptyString(source))
                    .filter((source): source is string => source !== undefined)
                : undefined;

            entries.push({
                type: "websearch",
                action: "search",
                query,
                status,
                sources,
                resultCount: typeof item.resultCount === "number" ? item.resultCount : undefined,
            });
            return;
        }

        if (item.action === "open_page") {
            const url = normalizeNonEmptyString(item.url);

            if (!url) {
                return;
            }

            entries.push({
                type: "websearch",
                action: "open_page",
                url,
                status,
                loc: typeof item.loc === "number" ? item.loc : undefined,
                numLines: typeof item.numLines === "number" ? item.numLines : undefined,
                totalLines: typeof item.totalLines === "number" ? item.totalLines : undefined,
                contentBytes: typeof item.contentBytes === "number" ? item.contentBytes : undefined,
            });
            return;
        }

        if (item.action === "find_in_page") {
            const pattern = normalizeNonEmptyString(item.pattern);
            const url = normalizeNonEmptyString(item.url);

            if (!pattern) {
                return;
            }

            entries.push({
                type: "websearch",
                action: "find_in_page",
                pattern,
                url,
                status,
                matchCount: typeof item.matchCount === "number" ? item.matchCount : undefined,
            });
        }

    });

    return entries;
}

function buildAiExtractorConfigRows(config?: VulSourceItemAiExtractorConfig): AiExtractorConfigRow[] {
    if (!config || typeof config !== "object") {
        return [];
    }

    const rows: AiExtractorConfigRow[] = [];

    const addStringRow = (label: string, value: unknown) => {
        const normalized = normalizeNonEmptyString(value);

        if (!normalized) {
            return;
        }

        rows.push({label, value: toDisplayValue(normalized)});
    };

    const type = normalizeNonEmptyString(config.type);
    if (type) {
        rows.push({
            label: "Type",
            value: formatAiExtractorConfigType(config, type)
        });
    }

    const source = normalizeNonEmptyString(config.source);
    if (source) {
        rows.push({
            label: "Source",
            value: AI_EXTRACTOR_SOURCES.includes(source) ? String(formatAiExtractorSource(source as AiExtractorSource)) : toDisplayValue(source)
        });
    }

    addStringRow("Model", config.model);
    addStringRow("Tier", config.openAiServiceTier);

    const reasoningEffort = getAiExtractorReasoningEffort(config);

    addStringRow("Reasoning", reasoningEffort);

    const webSearchValue = formatAiExtractorWebSearchConfig(config.webSearchMode, config.externalWebSearchBackend);

    if (webSearchValue) {
        rows.push({
            label: "Web search",
            value: webSearchValue
        });
    }

    return rows;
}

function getAiExtractorReasoningEffort(config: VulSourceItemAiExtractorConfig): string | undefined {
    switch (config.type) {
        case AiExtractorType.OPENAI_API:
        case AiExtractorType.OPENAI_COMPATIBLE_API:
            return config.openAiReasoningEffort;
        case AiExtractorType.ANTHROPIC_API:
            return config.anthropicReasoningEffort;
        case AiExtractorType.GROK_API:
            return config.grokReasoningEffort;
        default:
            return undefined;
    }
}

function formatAiExtractorConfigType(config: VulSourceItemAiExtractorConfig, type: string): string {
    const formattedType = AI_EXTRACTOR_TYPES.includes(type)
        ? String(formatAiExtractorType(type as AiExtractorType))
        : toDisplayValue(type);
    const providerName = normalizeNonEmptyString(config.aiProviderConnection?.name);

    if (type === AiExtractorType.OPENAI_COMPATIBLE_API && providerName) {
        return `${formattedType}: ${providerName}`;
    }

    return formattedType;
}

function buildToolTelemetrySummaryRows(toolTelemetry?: unknown): StatisticRow[] {
    const telemetry = toObjectRecord(toolTelemetry);

    if (!telemetry) {
        return [];
    }

    const rows: StatisticRow[] = [];
    const toolCallsTotal = toNumber(telemetry.toolCallsTotal) ?? toNumber(telemetry.webSearchCallCount);
    const executedRounds = toNumber(telemetry.executedRounds);
    const maxRounds = toNumber(telemetry.maxRounds);
    const reachedRoundLimit = telemetry.reachedRoundLimit === true;

    if (toolCallsTotal !== undefined) {
        rows.push({
            label: "Tool calls",
            value: formatCompactCount(toolCallsTotal)
        });
    }

    if (executedRounds !== undefined) {
        const roundLimitText = maxRounds !== undefined ? `${formatCompactCount(executedRounds)} / ${formatCompactCount(maxRounds)}` : formatCompactCount(executedRounds);

        rows.push({
            label: "Rounds",
            value: reachedRoundLimit ? `${roundLimitText} (limit reached)` : roundLimitText
        });
    }

    return rows;
}

function buildToolCallTooltipRows(toolTelemetry?: unknown): StatisticRow[] {
    const telemetry = toObjectRecord(toolTelemetry);

    if (!telemetry) {
        return [];
    }

    const rows: StatisticRow[] = [];

    expandBreakdownRows(rows, telemetry.toolCallsByName);
    expandBreakdownRows(rows, telemetry.toolCallsByAction);

    return rows;
}

function expandBreakdownRows(rows: StatisticRow[], value: unknown) {
    const breakdown = toObjectRecord(value);

    if (!breakdown) {
        return;
    }

    Object.entries(breakdown).forEach(([key, rawValue]) => {
        const numericValue = toNumber(rawValue);

        if (numericValue !== undefined) {
            rows.push({
                label: toDisplayValue(key),
                value: formatCompactCount(numericValue)
            });
        }
    });
}

function toObjectRecord(value: unknown): Record<string, unknown> | undefined {
    if (!value) {
        return undefined;
    }

    if (typeof value === "string") {
        try {
            return toObjectRecord(JSON.parse(value));
        } catch {
            return undefined;
        }
    }

    if (typeof value === "object" && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }

    return undefined;
}

function formatCompactCount(value: number): string {
    return TOKEN_COUNT_FORMATTER.format(value);
}

function toAssignmentKey(aiExtractionResult: AiExtractionResult, index: number): string {
    return aiExtractionResult.id
        ?? aiExtractionResult.aiExtractorId
        ?? `${aiExtractionResult.aiExtractorName}-${index}`;
}

function renderTextAiAttributeValue(aiAttributeRow: AiExtractionResultAiAttributeRow) {
    if (aiAttributeRow.kind === "texts" && (!aiAttributeRow.valueTexts || aiAttributeRow.valueTexts.length === 0)) {
        return <span className={styles["ai-extraction-attribute-empty-value"]}>no value</span>;
    }

    return aiAttributeRow.kind === "text"
        ? aiAttributeRow.valueText ?? null
        : aiAttributeRow.valueTexts?.join(", ") ?? null;
}

function formatOpenPageInfo(entry: ReasoningSummaryWebSearchOpenPageNode): string | null {
    const parts: string[] = [];

    if (entry.loc != null && entry.numLines != null && entry.totalLines != null) {
        if (entry.numLines > 0) {
            const lastLine = entry.loc + entry.numLines - 1;
            parts.push(`lines ${entry.loc}\u2013${lastLine} of ${entry.totalLines}`);
        } else {
            parts.push(`end of page (${entry.totalLines} lines)`);
        }
    }

    if (entry.contentBytes != null) {
        parts.push(formatBytesBinary(entry.contentBytes));
    }

    return parts.length > 0 ? parts.join(", ") : null;
}

function ReasoningEntry(props: { entry: ReasoningSummaryNode }) {
    const {entry} = props;

    if (entry.type === "reasoning") {
        return entry.text;
    }

    if (entry.type === "unknown") {
        const metadata = [entry.provider, entry.kind]
            .filter((value): value is string => typeof value === "string" && value.length > 0)
            .join(" / ");

        return (
            <>
                <span className={styles["ai-extraction-reasoning-label"]}>Unknown reasoning node</span>
                {metadata ? `: ${metadata}` : ""}
                {entry.raw !== undefined && (
                    <div className={styles["ai-extraction-reasoning-sources"]}>
                        <code>{JSON.stringify(entry.raw)}</code>
                    </div>
                )}
            </>
        );
    }

    if (entry.action === "search") {
        const resultInfo = entry.resultCount != null ? `${entry.resultCount} results` : entry.status;

        return (
            <>
                <span className={styles["ai-extraction-reasoning-label"]}>Web search:</span> {entry.query}
                {resultInfo && <span className={styles["ai-extraction-reasoning-status"]}>({resultInfo})</span>}
                {entry.sources && entry.sources.length > 0 && (
                    <div className={styles["ai-extraction-reasoning-sources"]}>
                        Sources: {entry.sources.join(", ")}
                    </div>
                )}
            </>
        );
    }

    if (entry.action === "open_page") {
        const pagingInfo = formatOpenPageInfo(entry);

        return (
            <>
                <span className={styles["ai-extraction-reasoning-label"]}>Open page:</span> {entry.url}
                {pagingInfo
                    ? <span className={styles["ai-extraction-reasoning-status"]}>[{pagingInfo}]</span>
                    : entry.status && <span className={styles["ai-extraction-reasoning-status"]}>({entry.status})</span>
                }
            </>
        );
    }

    if (entry.action === "find_in_page") {
        const matchInfo = entry.matchCount != null ? `${entry.matchCount} matches` : entry.status;

        return (
            <>
                <span className={styles["ai-extraction-reasoning-label"]}>Find in page:</span> "{entry.pattern}"{entry.url ? ` in ${entry.url}` : ""}
                {matchInfo && <span className={styles["ai-extraction-reasoning-status"]}>({matchInfo})</span>}
            </>
        );
    }

    return null;
}

function ConfigSection(props: { rows: AiExtractorConfigRow[]; assignmentKey: string }) {
    const {rows, assignmentKey} = props;

    if (rows.length === 0) {
        return null;
    }

    return (
        <div className={styles["ai-extraction-section"]}>
            <div className={styles["ai-extraction-config-tags"]}>
                {rows.map(row => (
                    <span key={`${assignmentKey}-aiExtractor-config-${row.label}`} className={styles["ai-extraction-config-item"]}>
                        {row.standalone ? (
                            <span className={styles["ai-extraction-config-label"]}>{row.label}</span>
                        ) : (
                            <>
                                <span className={styles["ai-extraction-config-label"]}>{row.label}:</span>
                                <span className={styles["ai-extraction-config-value"]}>{row.value}</span>
                            </>
                        )}
                    </span>
                ))}
            </div>
        </div>
    );
}

function AiAttributeMessageTooltip(props: { aiAttributeRow: AiExtractionResultAiAttributeRow; children: React.ReactElement }) {
    const {aiAttributeRow, children} = props;

    if (!aiAttributeRow.message) {
        return children;
    }

    const title = (
        <div className={styles["ai-attribute-message-tooltip"]}>
            <div className={styles["ai-attribute-message-tooltip-title"]}>Explanation</div>
            <div className={styles["ai-attribute-message-tooltip-body"]}>{aiAttributeRow.message}</div>
        </div>
    );

    return (
        <Tooltip title={title} overlayClassName={styles["ai-attribute-message-tooltip-overlay"]}>
            {children}
        </Tooltip>
    );
}

function AiAttributesSection(props: { aiAttributeRows: AiExtractionResultAiAttributeRow[]; showMessages: boolean }) {
    const {aiAttributeRows, showMessages} = props;

    if (aiAttributeRows.length === 0) {
        return (
            <div className={styles["ai-extraction-section"]}>
                <i className={styles["ai-extraction-empty"]}>No AI attribute results available.</i>
            </div>
        );
    }

    const isRatingRow = (row: AiExtractionResultAiAttributeRow) => row.kind === "rating" && typeof row.percent === "number";

    const ratingRows = aiAttributeRows.filter(isRatingRow);
    const otherRows = aiAttributeRows.filter(row => !isRatingRow(row));
    const hasRatingMessages = showMessages && ratingRows.some(row => row.message);

    return (
        <div className={styles["ai-extraction-section"]}>
            {ratingRows.length > 0 && (
                hasRatingMessages ? (
                    <div className={styles["ai-extraction-rating-stack"]}>
                        {ratingRows.map(aiAttributeRow => (
                            <div key={aiAttributeRow.name} className={styles["ai-extraction-rating-stack-item"]}>
                                <AiAttributeMessageTooltip aiAttributeRow={aiAttributeRow}>
                                    <AiAttributeRatingChip label={aiAttributeRow.name} value={(aiAttributeRow.percent ?? 0) / 100}/>
                                </AiAttributeMessageTooltip>
                                {aiAttributeRow.message && (
                                    <div className={styles["ai-extraction-attribute-message"]}>
                                        <MessageOutlined className={styles["ai-extraction-attribute-message-icon"]}/>
                                        <span>{aiAttributeRow.message}</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <AiAttributeRatingChipList>
                        {ratingRows.map(aiAttributeRow => (
                            <AiAttributeMessageTooltip key={aiAttributeRow.name} aiAttributeRow={aiAttributeRow}>
                                <AiAttributeRatingChip label={aiAttributeRow.name} value={(aiAttributeRow.percent ?? 0) / 100}/>
                            </AiAttributeMessageTooltip>
                        ))}
                    </AiAttributeRatingChipList>
                )
            )}
            {otherRows.length > 0 && (
                <div className={styles["ai-extraction-attribute-grid"]}>
                    {otherRows.map(aiAttributeRow => (
                        <React.Fragment key={aiAttributeRow.name}>
                            <AiAttributeMessageTooltip aiAttributeRow={aiAttributeRow}>
                                <span className={styles["ai-extraction-attribute-name"]}>{aiAttributeRow.name}:</span>
                            </AiAttributeMessageTooltip>
                            <span className={styles["ai-extraction-attribute-value-text"]}>
                                {renderTextAiAttributeValue(aiAttributeRow)}
                            </span>
                            {showMessages && aiAttributeRow.message && (
                                <div className={`${styles["ai-extraction-attribute-message"]} ${styles["ai-extraction-attribute-span-row"]}`}>
                                    <MessageOutlined className={styles["ai-extraction-attribute-message-icon"]}/>
                                    <span>{aiAttributeRow.message}</span>
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
}

function ReasoningSection(props: { entries: ReasoningSummaryNode[]; isVisible: boolean }) {
    const {entries, isVisible} = props;

    if (entries.length === 0 || !isVisible) {
        return null;
    }

    return (
        <div className={styles["ai-extraction-section"]}>
            <div className={styles["ai-extraction-section-title"]}>Reasoning</div>
            <Timeline
                className={styles["ai-extraction-reasoning-timeline"]}
                items={entries.map(entry => ({
                    children: <ReasoningEntry entry={entry}/>
                }))}
            />
        </div>
    );
}

function RawJsonSection(props: { aiExtractionResult: AiExtractionResult; isVisible: boolean; sizeLabel: string }) {
    const {aiExtractionResult, isVisible, sizeLabel} = props;

    if (!isVisible) {
        return null;
    }

    return (
        <div className={styles["ai-extraction-section"]}>
            <div className={styles["ai-extraction-section-title"]}>{`Raw JSON (${sizeLabel})`}</div>
            <div className={styles["ai-extraction-raw-panel"]}>
                <div
                    className={globalStyles["formatted-json"]}
                    dangerouslySetInnerHTML={{__html: syntaxHighlight(aiExtractionResult)}}
                />
            </div>
        </div>
    );
}

function AiExtractionResultCard(props: {
    aiExtractionResult: AiExtractionResult;
    index: number;
    actionMode: "none" | "delete" | "preview";
    onDeleteAiExtractionResult?: (aiExtractionResultId: string) => void;
    onRunAgain?: (aiExtractionResult: AiExtractionResult) => void;
    onPreviewRunAgain?: (aiExtractionResult: AiExtractionResult) => void;
    onPreviewCancel?: (aiExtractionResult: AiExtractionResult) => void;
    onPreviewSave?: (aiExtractionResult: AiExtractionResult) => void;
    onShowAiProviderCommunicationLog?: (runId: string) => void;
    isHighlighted?: boolean;
    onHighlightedAiExtractionResultRendered?: (element: HTMLDivElement | null) => void;
    globalMessagesVisible: boolean;
}) {
    const {
        aiExtractionResult,
        index,
        actionMode,
        onDeleteAiExtractionResult,
        onRunAgain,
        onPreviewRunAgain,
        onPreviewCancel,
        onPreviewSave,
        onShowAiProviderCommunicationLog,
        isHighlighted,
        onHighlightedAiExtractionResultRendered,
        globalMessagesVisible
    } = props;
    const assignmentKey = toAssignmentKey(aiExtractionResult, index);
    const aiAttributeRows = toAiAttributeRows(aiExtractionResult.aiAttributes);
    const reasoningSummary = buildReasoningSummary(aiExtractionResult.reasoningSummary);
    const systemPromptName = normalizeNonEmptyString(aiExtractionResult.aiExtractorConfig?.systemPrompt?.name);
    const supplementaryPromptName = normalizeNonEmptyString(aiExtractionResult.aiExtractorConfig?.supplementaryPrompt?.name);
    const aiExtractorConfigRows = buildAiExtractorConfigRows(aiExtractionResult.aiExtractorConfig);
    const statisticsRows = buildToolTelemetrySummaryRows(aiExtractionResult.toolTelemetry);
    const toolCallTooltipRows = buildToolCallTooltipRows(aiExtractionResult.toolTelemetry);
    const toolCallStatisticsValue = statisticsRows.find(row => row.label === "Tool calls")?.value;
    const roundStatisticsValue = statisticsRows.find(row => row.label === "Rounds")?.value;
    const cardClassName = isHighlighted
        ? `${styles["ai-extraction-card"]} ${styles["ai-extraction-card-highlight"]}`
        : styles["ai-extraction-card"];
    const showDeleteAction = actionMode === "delete" && onDeleteAiExtractionResult !== undefined;
    const showSavedRunAgainAction = actionMode === "delete" && onRunAgain !== undefined;
    const showPreviewActions = actionMode === "preview";
    const hasReasoning = reasoningSummary.length > 0;

    const [isMessagesVisible, setIsMessagesVisible] = React.useState(false);

    React.useEffect(() => {
        setIsMessagesVisible(globalMessagesVisible);
    }, [globalMessagesVisible]);

    const [isReasoningVisible, setIsReasoningVisible] = React.useState(false);
    const [isRawJsonVisible, setIsRawJsonVisible] = React.useState(false);

    const rawJsonSizeLabel = formatBytesBinary(utf8ByteLength(aiExtractionResult));

    const actionButtons = showPreviewActions ? (
        <div className={styles["ai-extraction-card-actions"]}>
            <Tooltip title={"Run again"}>
                <Button
                    size={"small"}
                    icon={<RedoOutlined/>}
                    aria-label={"Run again"}
                    onClick={() => onPreviewRunAgain?.(aiExtractionResult)}
                />
            </Tooltip>
            <Tooltip title={"Cancel preview"}>
                <Button
                    size={"small"}
                    icon={<CloseOutlined/>}
                    aria-label={"Cancel preview"}
                    onClick={() => onPreviewCancel?.(aiExtractionResult)}
                />
            </Tooltip>
            <Tooltip title={"Save AI extraction result"}>
                <Button
                    type={"primary"}
                    size={"small"}
                    icon={<SaveOutlined/>}
                    aria-label={"Save AI extraction result"}
                    disabled={!aiExtractionResult.aiExtractorId}
                    onClick={() => onPreviewSave?.(aiExtractionResult)}
                />
            </Tooltip>
        </div>
    ) : showDeleteAction || showSavedRunAgainAction ? (
        <div className={styles["ai-extraction-card-actions"]}>
            {showSavedRunAgainAction && (
                <Tooltip title={"Run again"}>
                    <Button
                        size={"small"}
                        icon={<RedoOutlined/>}
                        aria-label={"Run again"}
                        onClick={() => onRunAgain?.(aiExtractionResult)}
                    />
                </Tooltip>
            )}
            {showDeleteAction && (
                <Tooltip title={"Delete AI extraction result"}>
                    <Button
                        danger
                        size={"small"}
                        icon={<DeleteOutlined/>}
                        aria-label={"Delete AI extraction result"}
                        disabled={!aiExtractionResult.id}
                        onClick={() => aiExtractionResult.id && onDeleteAiExtractionResult(aiExtractionResult.id)}
                    />
                </Tooltip>
            )}
        </div>
    ) : undefined;
    const cardExtra = (
        <div className={styles["ai-extraction-card-extra"]}>
            <Tooltip title={`${isMessagesVisible ? "Hide" : "Show"} explanations`}>
                <Button
                    size={"small"}
                    type={isMessagesVisible ? "primary" : "default"}
                    icon={isMessagesVisible ? <MessageFilled/> : <MessageOutlined/>}
                    aria-label={`${isMessagesVisible ? "Hide" : "Show"} explanations`}
                    aria-pressed={isMessagesVisible}
                    onClick={() => setIsMessagesVisible(current => !current)}
                />
            </Tooltip>
            {hasReasoning && (
                <Tooltip title={`${isReasoningVisible ? "Hide" : "Show"} reasoning (${reasoningSummary.length})`}>
                    <Button
                        size={"small"}
                        type={isReasoningVisible ? "primary" : "default"}
                        icon={isReasoningVisible ? <BulbFilled/> : <BulbOutlined/>}
                        aria-label={`${isReasoningVisible ? "Hide" : "Show"} reasoning (${reasoningSummary.length})`}
                        aria-pressed={isReasoningVisible}
                        onClick={() => setIsReasoningVisible(current => !current)}
                    />
                </Tooltip>
            )}
            {aiExtractionResult.aiProviderCommunicationRunId && onShowAiProviderCommunicationLog && (
                <Tooltip title={"Show AI provider calls"}>
                    <Button
                        size={"small"}
                        icon={<FileTextOutlined/>}
                        aria-label={"Show AI provider calls"}
                        onClick={() => onShowAiProviderCommunicationLog(aiExtractionResult.aiProviderCommunicationRunId!)}
                    />
                </Tooltip>
            )}
            <Tooltip title={`${isRawJsonVisible ? "Hide" : "Show"} raw JSON (${rawJsonSizeLabel})`}>
                <Button
                    size={"small"}
                    type={isRawJsonVisible ? "primary" : "default"}
                    icon={<CodeOutlined/>}
                    aria-label={`${isRawJsonVisible ? "Hide" : "Show"} raw JSON (${rawJsonSizeLabel})`}
                    aria-pressed={isRawJsonVisible}
                    onClick={() => setIsRawJsonVisible(current => !current)}
                />
            </Tooltip>
            {actionButtons}
        </div>
    );

    return (
        <div
            ref={isHighlighted ? onHighlightedAiExtractionResultRendered : undefined}
        >
            <Card
                size={"small"}
                className={cardClassName}
                title={
                    <div className={styles["ai-extraction-card-title"]}>
                        <div className={styles["ai-extraction-card-title-row"]}>
                            <span className={styles["ai-extraction-card-title-main"]}>
                                {aiExtractionResult.aiExtractorId
                                    ? <Link to={`/definitions/ai-extractors/${aiExtractionResult.aiExtractorId}`}>
                                        {aiExtractionResult.aiExtractorName ?? "AI extractor"}
                                    </Link>
                                    : (aiExtractionResult.aiExtractorName ?? "AI extractor")
                                }
                            </span>
                            {systemPromptName && (
                                <Tag title={systemPromptName} className={styles["ai-extraction-prompt-tag"]}>{systemPromptName}</Tag>
                            )}
                            {supplementaryPromptName && (
                                <Tag title={supplementaryPromptName} className={styles["ai-extraction-prompt-tag"]}>+ {supplementaryPromptName}</Tag>
                            )}
                        </div>
                        <AiRunMetrics
                            timestampLabel="Extracted"
                            timestamp={aiExtractionResult.extractedAt}
                            tokenUsage={aiExtractionResult.tokenUsage}
                            executionTimeMs={aiExtractionResult.executionTimeMs}
                            priceBreakdown={aiExtractionResult.priceBreakdown}>
                            <AiRunMetric label="Tool calls" value={toolCallStatisticsValue} details={toolCallTooltipRows}/>
                            <AiRunMetric label="Rounds" value={roundStatisticsValue}/>
                        </AiRunMetrics>
                    </div>
                }
                extra={cardExtra}
            >
                <ConfigSection rows={aiExtractorConfigRows} assignmentKey={assignmentKey}/>
                <AiAttributesSection aiAttributeRows={aiAttributeRows} showMessages={isMessagesVisible}/>
                <ReasoningSection entries={reasoningSummary} isVisible={isReasoningVisible}/>
                <RawJsonSection aiExtractionResult={aiExtractionResult} isVisible={isRawJsonVisible} sizeLabel={rawJsonSizeLabel}/>
            </Card>
        </div>
    );
}

function AiExtractionResultsSection(props: Props) {
    const actionMode = props.actionMode ?? "delete";
    const globalMessagesVisible = props.globalMessagesVisible ?? false;

    if (!props.aiExtractionResults || props.aiExtractionResults.length === 0) {
        return null;
    }

    return (
        <div className={styles["ai-extraction-results-list"]}>
            {props.aiExtractionResults.map((aiExtractionResult: AiExtractionResult, index: number) => {
                const assignmentKey = toAssignmentKey(aiExtractionResult, index);
                const isHighlighted = props.highlightAiExtractionResultId !== undefined
                    && aiExtractionResult.id === props.highlightAiExtractionResultId;
                const canRunAgain = props.canRunAgain?.(aiExtractionResult) ?? false;
                const onRunAgain = canRunAgain ? props.onRunAgain : undefined;

                return (
                    <AiExtractionResultCard
                        key={assignmentKey}
                        aiExtractionResult={aiExtractionResult}
                        index={index}
                        actionMode={actionMode}
                        onDeleteAiExtractionResult={props.onDeleteAiExtractionResult}
                        onRunAgain={onRunAgain}
                        onPreviewRunAgain={props.onPreviewRunAgain}
                        onPreviewCancel={props.onPreviewCancel}
                        onPreviewSave={props.onPreviewSave}
                        onShowAiProviderCommunicationLog={props.onShowAiProviderCommunicationLog}
                        isHighlighted={isHighlighted}
                        onHighlightedAiExtractionResultRendered={props.onHighlightedAiExtractionResultRendered}
                        globalMessagesVisible={globalMessagesVisible}
                    />
                );
            })}
        </div>
    );
}

export default AiExtractionResultsSection;
